/**
 * Cafeteria module — tabs, pack/buy toggles, preference chips, balance editor,
 * and the menu / kid / history dialogs. The page is fully SSR'd; this file only
 * enhances it. All money and runway strings are patched from server responses —
 * no client-side money math beyond dollars→cents input parsing.
 */

import { csrfHeaders, getCsrfToken } from "./csrf";
import {
  addDays,
  applyImportedWeeks,
  blankWeekRows,
  createDraft,
  filledDayCount,
  importErrorMessage,
  markDirty,
  toRow,
  weeksToShow,
  shortDate,
  splitWeeksByMeal,
  weekHasContent,
  weekLabel,
  weekPayload,
  weeksToSave
} from "./cafeteria-menu-draft";
import { toast } from "./toast";
import { trackEvent } from "./ga-events";

const root = document.querySelector(".caf");

if (root) {
  // The bundle loads before the page's @section Scripts bootstrap, so the data
  // island must be read lazily at interaction time, never captured at module init.
  const getData = () => window.__cafeteriaData || {};
  let prefsDirty = false;

  // ── Small helpers ─────────────────────────────────────────────

  async function api(method, url, body) {
    const response = await fetch(url, {
      method,
      // Without Accept, an expired session answers a 302 to the sign-in page and fetch follows it to a
      // 200 of HTML — response.ok is true, the JSON parse quietly fails, and a lookup renders as "no
      // matches" instead of "you're signed out".
      headers: csrfHeaders({ Accept: "application/json" }),
      body: body === undefined ? undefined : JSON.stringify(body)
    });
    let json = null;
    try {
      json = await response.json();
    } catch {
      /* 204s and error pages have no JSON body */
    }
    return { ok: response.ok, status: response.status, json };
  }

  function errorMessage(result, fallback) {
    return (result.json && result.json.error) || fallback;
  }

  function dollarsToCents(value) {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
  }

  function setToneClass(el, prefix, tone) {
    el.classList.remove(`${prefix}-ok`, `${prefix}-warn`, `${prefix}-low`, `${prefix}-none`);
    el.classList.add(`${prefix}-${tone || "none"}`);
  }

  // ── Zero-state setup form ─────────────────────────────────────

  const setupForm = document.getElementById("caf-setup-form");
  if (setupForm) {
    setupForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      const message = document.getElementById("caf-setup-message");
      const name = document.getElementById("caf-setup-name").value.trim();
      if (!name) {
        message.textContent = "Give your kid a name to get started.";
        message.hidden = false;
        return;
      }

      const submit = document.getElementById("caf-setup-submit");
      submit.disabled = true;
      const result = await api("POST", "/api/cafeteria/kids", {
        displayName: name,
        schoolName: document.getElementById("caf-setup-school").value.trim() || null,
        grade: document.getElementById("caf-setup-grade").value.trim() || null,
        mealPriceCents: dollarsToCents(document.getElementById("caf-setup-price").value),
        initialBalanceCents: dollarsToCents(document.getElementById("caf-setup-balance").value)
      });
      submit.disabled = false;

      if (!result.ok) {
        message.textContent = errorMessage(result, "Couldn't add the kid — try again.");
        message.hidden = false;
        return;
      }

      trackEvent("cafeteria_kid_added", { context: "setup" });
      window.location.assign("/cafeteria");
    });
  }

  // ── View tabs ─────────────────────────────────────────────────

  const viewTabs = Array.from(root.querySelectorAll("[data-caf-tab]"));
  const panels = Array.from(root.querySelectorAll("[data-caf-panel]"));

  function activateView(view, options) {
    // Preference edits change every suggestion — a full navigation lets the
    // server recompute rather than leaving stale reasons on screen.
    if (prefsDirty && view !== "prefs" && !(options && options.skipDirtyCheck)) {
      const week = view === "week" && getData().weekStartIso ? `&week=${getData().weekStartIso}` : "";
      window.location.assign(`/cafeteria?view=${view}${week}`);
      return;
    }

    viewTabs.forEach((tab) => {
      const active = tab.dataset.cafTab === view;
      tab.setAttribute("aria-selected", active ? "true" : "false");
      tab.tabIndex = active ? 0 : -1;
    });
    panels.forEach((panel) => {
      panel.hidden = panel.dataset.cafPanel !== view;
    });

    const params = new URLSearchParams(window.location.search);
    params.set("view", view);
    window.history.replaceState(null, "", `${window.location.pathname}?${params}`);
    trackEvent("cafeteria_view_changed", { view });
  }

  function wireTablist(tabs, onActivate) {
    tabs.forEach((tab, index) => {
      tab.tabIndex = tab.getAttribute("aria-selected") === "true" ? 0 : -1;
      tab.addEventListener("keydown", (event) => {
        let target = null;
        if (event.key === "ArrowRight") target = tabs[(index + 1) % tabs.length];
        else if (event.key === "ArrowLeft") target = tabs[(index - 1 + tabs.length) % tabs.length];
        else if (event.key === "Home") target = tabs[0];
        else if (event.key === "End") target = tabs[tabs.length - 1];
        if (target) {
          event.preventDefault();
          target.focus();
          target.click();
        }
      });
    });
  }

  viewTabs.forEach((tab) => tab.addEventListener("click", () => activateView(tab.dataset.cafTab)));
  wireTablist(viewTabs);

  // ── Kid tabs (week view) ──────────────────────────────────────

  const kidTabs = Array.from(root.querySelectorAll("[data-caf-kid-tab]"));
  const kidPanels = Array.from(root.querySelectorAll("[data-caf-kid-panel]"));

  kidTabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      kidTabs.forEach((t) => {
        const active = t === tab;
        t.setAttribute("aria-selected", active ? "true" : "false");
        t.tabIndex = active ? 0 : -1;
      });
      kidPanels.forEach((panel) => {
        panel.hidden = panel.dataset.kidId !== tab.dataset.kidId;
      });
      trackEvent("cafeteria_kid_tab", {});
    });
  });
  wireTablist(kidTabs);

  // ── Pack/buy toggle ───────────────────────────────────────────

  function applyDecisionPatch(kidId, dateIso, payload) {
    const { day, week, wallet } = payload;

    root.querySelectorAll(`[data-caf-decision][data-kid-id="${kidId}"][data-date="${dateIso}"]`).forEach((btn) => {
      const active = btn.dataset.decision === day.effective;
      btn.classList.toggle("caf-seg-btn--active", active);
      btn.setAttribute("aria-checked", active ? "true" : "false");
      btn.tabIndex = active ? 0 : -1;
    });

    root.querySelectorAll(`[data-caf-switch][data-kid-id="${kidId}"][data-date="${dateIso}"]`).forEach((btn) => {
      const opposite = day.effective === "buy" ? "pack" : "buy";
      btn.dataset.decision = opposite;
      btn.textContent = `Switch to ${opposite}`;
    });

    root.querySelectorAll(`[data-caf-day-badge][data-kid-id="${kidId}"][data-date="${dateIso}"]`).forEach((badge) => {
      badge.classList.remove("caf-badge--buy", "caf-badge--pack", "caf-badge--alert");
      badge.classList.add(`caf-badge--${day.effective}`);
      if (day.allergenConflict) badge.classList.add("caf-badge--alert");
      badge.textContent = (day.effective === "buy" ? "Buy" : "Pack") + (day.allergenConflict ? " · allergen" : "");
    });

    root.querySelectorAll(`[data-caf-day-reason][data-kid-id="${kidId}"][data-date="${dateIso}"]`).forEach((reason) => {
      reason.textContent = day.reasonText;
    });

    root.querySelectorAll(`[data-caf-day-funds][data-kid-id="${kidId}"][data-date="${dateIso}"]`).forEach((chip) => {
      chip.hidden = !day.insufficientFunds;
    });

    if (payload.days) {
      payload.days.forEach((weekDay) => {
        root.querySelectorAll(`[data-caf-day-funds][data-kid-id="${kidId}"][data-date="${weekDay.dateIso}"]`).forEach((chip) => {
          chip.hidden = !weekDay.insufficientFunds;
        });
      });
    }

    // The rendered Week panel may be showing a different week than the toggled
    // day's (e.g. a Today-card toggle while browsing next week) — never clobber
    // its summary with another week's numbers.
    const summary = root.querySelector(`[data-caf-summary][data-kid-id="${kidId}"]`);
    const summaryMatchesWeek = summary && (!payload.weekStartIso || summary.dataset.weekStart === payload.weekStartIso);
    if (summary && summaryMatchesWeek && week) {
      summary.querySelector("[data-caf-sum-buy]").textContent = week.buyDays;
      const buyWord = summary.querySelector("[data-caf-sum-buy-word]");
      if (buyWord) buyWord.textContent = week.buyDays === 1 ? "day" : "days";
      summary.querySelector("[data-caf-sum-pack]").textContent = week.packDays;
      summary.querySelector("[data-caf-sum-spend]").textContent = week.estSpendDisplay;
      const endWrap = summary.querySelector("[data-caf-sum-end-wrap]");
      const endSep = summary.querySelector("[data-caf-sum-end-sep]");
      const end = summary.querySelector("[data-caf-sum-end]");
      const hasEnd = week.endBalanceDisplay !== null && week.endBalanceDisplay !== undefined;
      if (endWrap) endWrap.hidden = !hasEnd;
      if (endSep) endSep.hidden = !hasEnd;
      if (end && hasEnd) {
        end.textContent = week.endBalanceDisplay;
        setToneClass(end, "caf-tone", week.endBalanceTone);
      }
    }

    if (wallet) applyWalletPatch(kidId, wallet);
  }

  function applyWalletPatch(kidId, wallet) {
    const card = root.querySelector(`[data-caf-wallet][data-kid-id="${kidId}"]`);
    if (!card) return;

    // The headline figure's tone tracks the runway, so retune it alongside the bar.
    const display = card.querySelector("[data-caf-balance-display]");
    if (display) setToneClass(display, "caf-tone", wallet.hasForecast ? wallet.tone : "none");

    const fill = card.querySelector(".caf-runway-fill");
    if (fill) {
      fill.style.width = `${wallet.hasForecast ? wallet.barPct : 0}%`;
      fill.classList.remove("caf-tone-bg-ok", "caf-tone-bg-warn", "caf-tone-bg-low", "caf-tone-bg-none");
      fill.classList.add(`caf-tone-bg-${wallet.hasForecast ? wallet.tone : "none"}`);
    }
    card.querySelector("[data-caf-runway]").classList.toggle("caf-runway--empty", !wallet.hasForecast);

    const runwayMeta = card.querySelector("[data-caf-runway-meta]");
    if (runwayMeta) runwayMeta.textContent = wallet.metaLabel || "";

    const sep = card.querySelector(".caf-meta-sep");
    const updated = card.querySelector("[data-caf-balance-updated]");
    if (sep) sep.hidden = !(updated && updated.textContent.trim() && wallet.metaLabel);

    const emptyLabel = card.querySelector("[data-caf-runway-label]");
    if (emptyLabel) {
      emptyLabel.textContent = wallet.emptyLabel || "";
      emptyLabel.hidden = !wallet.emptyLabel;
    }
  }

  function captureDecisionState(kidId, dateIso) {
    const active = root.querySelector(
      `[data-caf-decision][data-kid-id="${kidId}"][data-date="${dateIso}"].caf-seg-btn--active`);
    return active ? active.dataset.decision : null;
  }

  function applyOptimisticDecision(kidId, dateIso, decision) {
    root.querySelectorAll(`[data-caf-decision][data-kid-id="${kidId}"][data-date="${dateIso}"]`).forEach((btn) => {
      const active = btn.dataset.decision === decision;
      btn.classList.toggle("caf-seg-btn--active", active);
      btn.setAttribute("aria-checked", active ? "true" : "false");
      btn.tabIndex = active ? 0 : -1;
    });
  }

  // Radio-group keyboard pattern for the Buy/Pack segments: the checked segment is
  // the tab stop, arrows move and activate within the pair.
  root.querySelectorAll("[data-caf-decision]").forEach((btn) => {
    btn.tabIndex = btn.getAttribute("aria-checked") === "true" ? 0 : -1;
  });
  root.addEventListener("keydown", (event) => {
    const seg = event.target.closest("[data-caf-decision]");
    if (!seg) return;
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    const sibling = Array.from(seg.closest(".caf-seg").querySelectorAll("[data-caf-decision]"))
      .find((btn) => btn !== seg);
    if (sibling) {
      sibling.focus();
      sibling.click();
    }
  });

  // One in-flight save per (kid, day): a second click mid-request would capture the
  // optimistic state as "previous" and could revert to a state the server never had.
  const pendingDecisions = new Set();

  async function setDecision(kidId, dateIso, decision) {
    const pendingKey = `${kidId}|${dateIso}`;
    if (pendingDecisions.has(pendingKey)) return;
    pendingDecisions.add(pendingKey);

    const previous = captureDecisionState(kidId, dateIso);
    applyOptimisticDecision(kidId, dateIso, decision);

    try {
      const result = await api("PUT", `/api/cafeteria/decisions/${encodeURIComponent(kidId)}/${dateIso}`, { decision });
      if (!result.ok || !result.json) {
        if (previous) applyOptimisticDecision(kidId, dateIso, previous);
        toast.error(errorMessage(result, "Couldn't save that — try again."));
        return;
      }

      applyDecisionPatch(kidId, dateIso, result.json);
      trackEvent("cafeteria_decision_toggled", { decision });
    } finally {
      pendingDecisions.delete(pendingKey);
    }
  }

  // ── Balance editor ────────────────────────────────────────────

  function openBalanceEditor(card) {
    const form = card.querySelector("[data-caf-balance-form]");
    const view = card.querySelector("[data-caf-balance-view]");
    if (!form || !view) return;
    view.hidden = true;
    form.hidden = false;
    const input = form.querySelector("input");
    input.focus();
    input.select();
  }

  function closeBalanceEditor(card) {
    const form = card.querySelector("[data-caf-balance-form]");
    const view = card.querySelector("[data-caf-balance-view]");
    if (!form || !view) return;
    const hadFocus = form.contains(document.activeElement);
    form.hidden = true;
    view.hidden = false;
    // Hiding the form would drop keyboard focus to <body>; hand it back to the trigger.
    if (hadFocus) {
      const editBtn = card.querySelector("[data-caf-balance-edit]");
      if (editBtn) editBtn.focus();
    }
  }

  root.querySelectorAll("[data-caf-balance-form]").forEach((form) => {
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const kidId = form.dataset.kidId;
      const cents = dollarsToCents(form.querySelector("input").value);
      if (cents === null) {
        toast.error("Enter a balance amount first.");
        return;
      }

      const result = await api("PUT", `/api/cafeteria/kids/${encodeURIComponent(kidId)}/balance`, {
        newBalanceCents: cents
      });
      if (!result.ok || !result.json) {
        toast.error(errorMessage(result, "Couldn't update the balance — try again."));
        return;
      }

      const payload = result.json;
      const card = root.querySelector(`[data-caf-wallet][data-kid-id="${kidId}"]`);
      if (card) {
        const display = card.querySelector("[data-caf-balance-display]");
        display.textContent = payload.balanceDisplay;
        const updated = card.querySelector("[data-caf-balance-updated]");
        if (updated) updated.textContent = payload.balanceUpdatedLabel || "";
        const editBtn = card.querySelector("[data-caf-balance-edit]");
        if (editBtn) editBtn.textContent = "Update balance";
        form.querySelector("input").value = payload.balanceInputValue;
        applyWalletPatch(kidId, payload.wallet);
        closeBalanceEditor(card);
      }

      // The SSR banner describes one specific kid — only touch it when THIS kid is
      // the one it points at; other kids' banners stay truthful as rendered.
      const banner = root.querySelector("[data-caf-low-banner]");
      if (banner && banner.querySelector(`[data-caf-focus-balance][data-kid-id="${kidId}"]`)) {
        if (payload.lowBalanceMessage) {
          banner.querySelector(".caf-banner-copy").textContent = payload.lowBalanceMessage;
        } else {
          banner.hidden = true;
        }
      }

      toast.success("Balance updated.");
      trackEvent("cafeteria_balance_updated", {});
    });
  });

  // ── Preference chips ──────────────────────────────────────────

  function collectTerms(prefCard, kind) {
    const group = prefCard.querySelector(`[data-caf-pref-group][data-kind="${kind}"]`);
    return Array.from(group.querySelectorAll("[data-caf-chip]")).map((chip) => chip.dataset.term);
  }

  function renderChips(prefCard, kind, terms) {
    const group = prefCard.querySelector(`[data-caf-pref-group][data-kind="${kind}"]`);
    const row = group.querySelector("[data-caf-chip-row]");
    row.querySelectorAll("[data-caf-chip]").forEach((chip) => chip.remove());
    const addBtn = row.querySelector("[data-caf-chip-add]");
    terms.forEach((term) => {
      const chip = document.createElement("span");
      chip.className = `caf-chip caf-chip--${kind}`;
      chip.dataset.cafChip = "";
      chip.dataset.term = term;
      chip.append(document.createTextNode(term));
      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "caf-chip-remove";
      remove.dataset.cafChipRemove = "";
      remove.setAttribute("aria-label", `Remove ${term}`);
      remove.innerHTML = "&times;";
      chip.append(remove);
      row.insertBefore(chip, addBtn);
    });
  }

  async function savePreferences(prefCard, changedKind) {
    const kidId = prefCard.dataset.kidId;
    const body = {
      favorites: collectTerms(prefCard, "favorites"),
      dislikes: collectTerms(prefCard, "dislikes"),
      allergens: collectTerms(prefCard, "allergens")
    };

    const result = await api("PUT", `/api/cafeteria/kids/${encodeURIComponent(kidId)}/preferences`, body);
    if (!result.ok || !result.json) {
      toast.error(errorMessage(result, "Couldn't save preferences — try again."));
      return false;
    }

    renderChips(prefCard, "favorites", result.json.favorites);
    renderChips(prefCard, "dislikes", result.json.dislikes);
    renderChips(prefCard, "allergens", result.json.allergens);
    prefsDirty = true;
    trackEvent("cafeteria_pref_saved", { kind: changedKind });
    return true;
  }

  root.querySelectorAll("[data-caf-chip-input]").forEach((input) => {
    input.addEventListener("keydown", async (event) => {
      if (event.key === "Escape") {
        input.hidden = true;
        input.value = "";
        return;
      }
      if (event.key !== "Enter") return;
      event.preventDefault();

      const term = input.value.trim();
      if (!term) {
        input.hidden = true;
        return;
      }

      const prefCard = input.closest("[data-caf-prefs]");
      const group = input.closest("[data-caf-pref-group]");
      const kind = group.dataset.kind;
      const terms = collectTerms(prefCard, kind);
      input.value = "";
      if (!terms.some((t) => t.toLowerCase() === term.toLowerCase())) {
        renderChips(prefCard, kind, terms.concat(term));
        if (!(await savePreferences(prefCard, kind))) {
          renderChips(prefCard, kind, terms);
        }
      }
      // No focus() here: the input keeps focus naturally, and yanking it back after
      // the await would steal focus from wherever the user moved mid-save.
    });
    input.addEventListener("blur", () => {
      if (!input.value.trim()) input.hidden = true;
    });
  });

  // ── Dialog plumbing ───────────────────────────────────────────

  const menuDialog = document.getElementById("caf-menu-dialog");
  const kidDialog = document.getElementById("caf-kid-dialog");
  const confirmDialog = document.getElementById("caf-confirm-dialog");
  const historyDialog = document.getElementById("caf-history-dialog");

  document.querySelectorAll("[data-caf-dialog-close]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const dialog = btn.closest("dialog");
      if (dialog) dialog.close();
    });
  });

  // ── Menu dialog ───────────────────────────────────────────────
  //
  // The dialog edits one week at a time, but an uploaded file usually covers several — a district
  // publishes the month, not the week. So the fields are backed by a draft keyed by week rather than
  // read straight off the DOM: switching weeks stashes what is on screen and restores what was there,
  // and Save writes every week the parent has content for.
  //
  // Nothing an import returns is written on its own. It fills these fields, the parent reads them, and
  // the existing menu-week API stores what they approve — so a misread costs an edit, not a bad week.

  // One draft per meal, same shape each (./cafeteria-menu-draft owns the rules and has the tests).
  // `menuDraft` always aliases the ACTIVE meal's draft so every existing render/capture path stays
  // meal-agnostic; switching meals just repoints it. Lunch is the planning meal and the dialog's default.
  let menuDrafts = { lunch: null, breakfast: null };
  let activeMeal = "lunch";
  let menuDraft = null;

  function setActiveMeal(meal) {
    activeMeal = meal;
    menuDraft = menuDrafts[meal];
    document.querySelectorAll("[data-caf-meal]").forEach((btn) => {
      btn.setAttribute("aria-pressed", String(btn.dataset.cafMeal === meal));
    });
  }

  /** "· 5 days" on each meal tab — how an import that filled the OTHER meal stays discoverable. */
  function updateMealBadges() {
    ["lunch", "breakfast"].forEach((meal) => {
      const badge = document.querySelector(`[data-caf-meal-count="${meal}"]`);
      const draft = menuDrafts[meal];
      if (!badge || !draft) return;
      const filled = draft.order.reduce((sum, iso) => sum + filledDayCount(draft.weeks[iso]), 0);
      badge.textContent = filled > 0 ? ` · ${filled} ${filled === 1 ? "day" : "days"}` : "";
    });
  }

  function menuSchoolKey() {
    const select = document.getElementById("caf-menu-school");
    if (select) return select.value;
    const schools = getData().schools;
    return (schools && schools[0] && schools[0].schoolKey) || null;
  }

  /** Rows for one week + meal from the server's saved-menu prefill, or blanks when none is stored. */
  function rowsFromPrefill(weekStartIso, meal) {
    const schoolKey = menuSchoolKey();
    const prefill = (getData().menuPrefills || []).find(
      (m) => m.weekStartIso === weekStartIso && m.schoolKey === schoolKey && (m.meal || "lunch") === meal);

    return blankWeekRows().map((row, index) => {
      const day = prefill && prefill.days.find((d) => d.dateIso === addDays(weekStartIso, index));
      return day ? toRow(day) : row;
    });
  }

  /** Whether a saved menu exists for a week — the difference between clearing one and never having one. */
  function hasStoredMenu(weekStartIso, meal) {
    const schoolKey = menuSchoolKey();
    return (getData().menuPrefills || []).some(
      (m) => m.weekStartIso === weekStartIso
        && m.schoolKey === schoolKey
        && (m.meal || "lunch") === meal
        && (m.days || []).length > 0);
  }

  /**
   * Commits anything still sitting un-entered in an "add" box.
   *
   * The chips ARE the draft now — adding and removing mutate it directly, so there is no DOM state to
   * copy back. What is left is the half-typed dish a parent walked away from: they typed "Corn Dog" and
   * clicked Save without pressing Enter, and losing it silently would be the worst kind of small bug.
   */
  function captureActiveWeek() {
    if (!menuDraft || !menuDialog) return;
    const rows = menuDraft.weeks[menuDraft.active];
    if (!rows) return;

    menuDialog.querySelectorAll("[data-caf-mday-input]").forEach((input) => {
      const pending = input.value.trim();
      if (!pending) return;
      const index = Number(input.closest("[data-caf-mday]").dataset.cafMday);
      // A refusal here (full list) leaves the text in place rather than dropping it silently.
      if (addMenuItem(rows[index], input.dataset.cafMdayInput, pending) === null) input.value = "";
    });
  }

  // Mirrors CafeteriaService.MaxMenuItemsPerList. The server truncates past this; a dish accepted on
  // screen and dropped on save is exactly the silent cap this module refuses to ship.
  const MAX_ITEMS_PER_LIST = 10;

  /** Appends a dish to a day. Returns why it was refused, or null when it went in. */
  function addMenuItem(row, group, value) {
    const text = (value || "").trim();
    if (!text) return "blank";

    const list = row[group];
    if (list.some((existing) => existing.toLowerCase() === text.toLowerCase())) return "duplicate";
    if (list.length >= MAX_ITEMS_PER_LIST) return "full";

    list.push(text);
    return null;
  }

  /** Paints the active week's rows into the fields and retitles the day legends. */
  function renderActiveWeek() {
    if (!menuDraft || !menuDialog) return;
    const weekStartIso = menuDraft.active;
    const rows = menuDraft.weeks[weekStartIso] || blankWeekRows();

    const label = menuDialog.querySelector("[data-caf-menu-week-label]");
    if (label) label.textContent = `Week of ${weekLabel(weekStartIso)}`;

    menuDialog.querySelectorAll("[data-caf-mday]").forEach((fieldset) => {
      const index = Number(fieldset.dataset.cafMday);
      const dateIso = addDays(weekStartIso, index);
      fieldset.dataset.date = dateIso;
      fieldset.querySelector("[data-caf-mday-label]").textContent = shortDate(dateIso);
      // The visible label is aria-hidden, so the legend needs its own copy of the date.
      const legendDate = fieldset.querySelector("[data-caf-mday-legend-date]");
      if (legendDate) legendDate.textContent = shortDate(dateIso);
      paintChips(fieldset, rows[index]);
      paintNoSchool(fieldset, !!rows[index].noSchool);
    });
  }

  /** One chip per dish, rebuilt from the draft row. */
  function paintChips(fieldset, row) {
    ["entrees", "sides"].forEach((group) => {
      const host = fieldset.querySelector(`[data-caf-mday-chips="${group}"]`);
      host.textContent = "";
      row[group].forEach((item, position) => {
        const chip = document.createElement("span");
        chip.className = `caf-chip caf-chip--menu caf-chip--${group}`;
        chip.append(document.createTextNode(item));

        const remove = document.createElement("button");
        remove.type = "button";
        remove.className = "caf-chip-remove";
        remove.dataset.cafMdayRemove = String(position);
        remove.dataset.group = group;
        remove.setAttribute("aria-label", `Remove ${item}`);
        remove.innerHTML = "&times;";
        chip.append(remove);
        host.append(chip);
      });
    });
  }

  /** One day row's closed state: fields disabled (text kept as the undo buffer), toggle re-labeled. */
  function paintNoSchool(fieldset, closed) {
    fieldset.classList.toggle("caf-menu-day--noschool", closed);
    fieldset.querySelectorAll("[data-caf-mday-input]").forEach((input) => { input.disabled = closed; });
    fieldset.querySelectorAll("[data-caf-mday-remove]").forEach((btn) => { btn.disabled = closed; });
    const toggle = fieldset.querySelector("[data-caf-mday-noschool]");
    if (toggle) {
      toggle.textContent = closed ? "School's closed — undo" : "No school";
      toggle.setAttribute("aria-pressed", String(closed));
      // A closure is a fact about the day, not a meal. It lives on the lunch draft (the planning
      // meal), which also drives the day's rendering — a per-meal pair of toggles would just invite
      // the two to disagree.
      toggle.hidden = activeMeal !== "lunch";
    }
  }

  function selectWeek(weekStartIso) {
    if (!menuDraft || weekStartIso === menuDraft.active) return;
    captureActiveWeek();
    menuDraft.active = weekStartIso;
    renderActiveWeek();
    // Update in place rather than rebuild: replacing the chip a keyboard user just activated destroys
    // the element holding focus, dropping them back to the top of the document mid-review.
    updateWeekChipStates();
    updateSaveLabel();
  }

  /**
   * The week strip, shown only once a file has produced more than the week the dialog opened on.
   * A day count per chip is what tells a parent at a glance which weeks came back thin.
   */
  /** "5 days" / "1 day" / "empty" — the count that tells a parent which weeks came back thin. */
  function chipCountLabel(filled) {
    if (!filled) return "empty";
    return `${filled} ${filled === 1 ? "day" : "days"}`;
  }

  /**
   * Refreshes each chip's pressed state and day count without touching the DOM structure.
   * The label is set explicitly because the visible text is two spans, and a screen reader reads their
   * concatenation — "Aug 31 – Sep 45 days" rather than a week and a count.
   */
  function updateWeekChipStates() {
    if (!menuDraft) return;
    document.querySelectorAll("[data-caf-import-week]").forEach((chip) => {
      const iso = chip.dataset.cafImportWeek;
      const rows = menuDraft.weeks[iso];
      if (!rows) return;

      const filled = filledDayCount(rows);
      chip.setAttribute("aria-pressed", String(iso === menuDraft.active));
      chip.setAttribute("aria-label", `Week of ${weekLabel(iso)}, ${chipCountLabel(filled)}`);
      chip.querySelector(".caf-import-week-chip-count").textContent = chipCountLabel(filled);
    });
  }

  function renderWeekChips() {
    const container = document.querySelector("[data-caf-import-weeks]");
    const chips = document.querySelector("[data-caf-import-week-chips]");
    if (!container || !chips || !menuDraft) return;

    // Only the weeks worth reviewing: what the file produced, plus the opened week when it actually
    // holds something. Listing the untouched target here is what told a parent their one-week PDF
    // contained two weeks, the second of them "empty".
    const weeks = weeksToShow(menuDraft);

    // One week is just the week the dialog opened on — a strip of one is noise.
    if (weeks.length < 2) {
      container.hidden = true;
      chips.textContent = "";
      return;
    }

    container.hidden = false;
    chips.textContent = "";
    weeks.forEach((iso) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "caf-import-week-chip";
      button.dataset.cafImportWeek = iso;

      const name = document.createElement("span");
      name.className = "caf-import-week-chip-name";
      name.textContent = weekLabel(iso);
      const count = document.createElement("span");
      count.className = "caf-import-week-chip-count";

      button.appendChild(name);
      button.appendChild(count);
      chips.appendChild(button);
    });

    updateWeekChipStates();
  }

  function openMenuDialog(weekStart, schoolKey) {
    if (!menuDialog) return;
    const select = document.getElementById("caf-menu-school");
    if (select && schoolKey) select.value = schoolKey;

    menuDrafts = {
      lunch: createDraft(weekStart, rowsFromPrefill(weekStart, "lunch"), hasStoredMenu(weekStart, "lunch")),
      breakfast: createDraft(weekStart, rowsFromPrefill(weekStart, "breakfast"), hasStoredMenu(weekStart, "breakfast"))
    };
    setActiveMeal("lunch");
    updateMealBadges();

    resetImportUi();
    const revealLine = document.querySelector("[data-caf-import-reveal]");
    if (revealLine) revealLine.dataset.revealed = "";
    finderReopened = null;
    resetPicker();
    renderProviderUi();
    const message = document.getElementById("caf-menu-message");
    message.hidden = true;
    renderActiveWeek();
    renderWeekChips();
    updateSaveLabel();
    menuDialog.showModal();
  }

  const menuSchoolSelect = document.getElementById("caf-menu-school");
  if (menuSchoolSelect) {
    menuSchoolSelect.addEventListener("change", () => {
      if (!menuDraft) return;
      // Re-prefilling from the newly chosen school's saved menu would throw away an import. Once a file
      // has been read, changing the school means "this menu belongs to that school" — keep the values.
      ["lunch", "breakfast"].forEach((meal) => {
        const draft = menuDrafts[meal];
        if (!draft || draft.imported) return;
        draft.weeks[draft.target] = rowsFromPrefill(draft.target, meal);
      });
      renderActiveWeek();
      updateMealBadges();
      // The picker holds a selection made for the PREVIOUS school. Left standing, Connect would link
      // this school to that school's provider id — silently, and with no way to notice.
      resetPicker();
      finderReopened = null;
      renderProviderUi();
    });
  }

  // ── Menu provider (SchoolCafe) ────────────────────────────────
  //
  // Structured data straight from the school's own platform: no file, no AI, free and exact. The fetch
  // returns the import-response shape, so everything downstream of the wire — meal routing, drafts,
  // badges, banner, multi-week save — is the same code the file import runs.

  const providerRoot = document.querySelector("[data-caf-provider]");
  // Read lazily, never captured at init: the bundle executes above the page's data-island script, so an
  // eager copy is always empty — which silently showed every linked school the connect form again on
  // each visit. `providerLinksOverride` layers this session's connects/disconnects over the island.
  let providerLinksOverride = null;
  let providerBusy = false;

  function allProviderLinks() {
    return providerLinksOverride || ((getData().provider || {}).links || []);
  }

  function providerLinkFor(schoolKey) {
    return allProviderLinks().find((l) => l.schoolKey === schoolKey) || null;
  }

  // Remembered per school, because "my school isn't on there" is a fact about the school, not a mood.
  const FINDER_DISMISS_KEY = "caf-finder-dismissed";

  function dismissedSchools() {
    try {
      // Array-checked, not just parse-checked: a stored value that is valid JSON but not an array (an
      // older format, or another tab) would make the .includes() below throw out of providerState(),
      // leaving the dialog half-rendered.
      const parsed = JSON.parse(window.localStorage.getItem(FINDER_DISMISS_KEY) || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function dismissFinder(schoolKey) {
    try {
      const all = dismissedSchools();
      if (!all.includes(schoolKey)) all.push(schoolKey);
      window.localStorage.setItem(FINDER_DISMISS_KEY, JSON.stringify(all.slice(-20)));
    } catch {
      /* private browsing — the finder simply asks again next time */
    }
  }

  // The school whose finder was reopened by hand, or null. Per school rather than a bare flag: for a
  // two-school family, reopening for one must not override the other's recorded dismissal. Cleared on
  // every dialog open — reopening is a deliberate act for this visit, not a standing preference.
  let finderReopened = null;

  /**
   * Which of four states the menu dialog is in, all decided by one question: has this parent already
   * got a way to get their menu in?
   *
   *  linked    — connected. Fetch button; the file card collapses behind a line.
   *  only      — blank slate. The finder IS the dialog: an empty week grid and a Save button under it
   *              are three competing answers to "how do I get my menu in", and the fastest one wins by
   *              being the only one on screen.
   *  offered   — they already have menus but never connected. The finder sits above their normal
   *              dialog as an offer, never in front of the task.
   *  dismissed — they said their school isn't listed. One quiet line back, asked once, not weekly.
   */
  /**
   * Empties the school finder back to "choose a state".
   *
   * Called whenever the school this dialog is about changes — on open, and when a two-school family
   * switches the School dropdown. A picker left populated from the previous school is not just stale
   * UI: Connect sends the school key read at click time with the provider id sitting in the select, so
   * it would link this school to the other one's SchoolCafe school, silently and unnoticeably.
   */
  function resetPicker() {
    if (!providerRoot) return;

    const state = providerRoot.querySelector("[data-caf-provider-state]");
    const district = providerRoot.querySelector("[data-caf-provider-district]");
    const results = providerRoot.querySelector("[data-caf-provider-district-results]");
    const status = providerRoot.querySelector("[data-caf-provider-district-status]");
    const school = providerRoot.querySelector("[data-caf-provider-school]");
    const grade = providerRoot.querySelector("[data-caf-provider-grade]");
    const connect = providerRoot.querySelector("[data-caf-provider-connect-btn]");

    if (state) state.value = "";
    if (district) district.value = "";
    if (results) { results.textContent = ""; results.hidden = true; }
    if (status) { status.textContent = ""; status.hidden = true; }
    if (school) school.textContent = "";
    if (grade) grade.textContent = "";
    if (connect) connect.disabled = true;

    ["district", "school"].forEach((name) => {
      const step = providerRoot.querySelector(`[data-caf-picker-step="${name}"]`);
      if (step) step.hidden = true;
    });
  }

  function providerState() {
    const schoolKey = menuSchoolKey();
    if (providerLinkFor(schoolKey)) return "linked";
    if (finderReopened === schoolKey) return "offered";
    if (dismissedSchools().includes(schoolKey)) return "dismissed";

    // Saved menus for THIS school (another school's menus say nothing about this one), plus anything
    // sitting unsaved in the draft — a fetch that filled two weeks and was then disconnected must not
    // take the day grid, and the Save button, down with it.
    const saved = (getData().menuPrefills || [])
      .some((m) => m.schoolKey === schoolKey && (m.days || []).length > 0);
    const drafted = ["lunch", "breakfast"]
      .some((meal) => menuDrafts[meal] && Object.values(menuDrafts[meal].weeks).some(weekHasContent));

    return saved || drafted ? "offered" : "only";
  }

  /** Shows fetch-mode for a linked school, the finder otherwise. Re-run on open and school change. */
  function renderProviderUi() {
    if (!providerRoot) return;
    const state = providerState();
    const linked = state === "linked";
    const finderOnly = state === "only";

    providerRoot.hidden = false;
    providerRoot.querySelector("[data-caf-provider-linked]").hidden = !linked;
    providerRoot.querySelector("[data-caf-provider-connect]").hidden = linked || state === "dismissed";

    const reopen = document.querySelector("[data-caf-provider-reopen]");
    if (reopen) reopen.hidden = state !== "dismissed";

    // Linked schools collapse the file-drop card behind one quiet line: the connector is the primary
    // path then, and the reveal is a single click for the odd week that still needs a file.
    const importCard = document.querySelector("[data-caf-import]");
    const reveal = document.querySelector("[data-caf-import-reveal]");
    if (importCard && reveal) {
      const revealed = reveal.dataset.revealed === "1";
      importCard.hidden = finderOnly || (linked && !revealed);
      reveal.hidden = finderOnly || !linked || revealed;
    }

    ["[data-caf-meal-toggle]", "[data-caf-menu-days]", "[data-caf-menu-week-label]"].forEach((selector) => {
      const el = document.querySelector(selector);
      if (el) el.hidden = finderOnly;
    });

    const save = document.getElementById("caf-menu-save");
    if (save) save.hidden = finderOnly;

    // The week strip owns its own visibility (it appears only once a file produced more than one week);
    // finder-only just forces it down.
    if (finderOnly) {
      const weeks = document.querySelector("[data-caf-import-weeks]");
      if (weeks) weeks.hidden = true;
    }

    if (linked) {
      providerRoot.querySelector("[data-caf-provider-meta]").textContent =
        `Grade ${providerLinkFor(menuSchoolKey()).grade}`;
    }
  }

  /** Shared landing for provider weeks — identical to the import's apply path. */
  function applyProviderWeeks(json) {
    captureActiveWeek();
    const byMeal = splitWeeksByMeal(json.weeks || []);
    const lunchDays = applyImportedWeeks(menuDrafts.lunch, byMeal.lunch);
    const breakfastDays = applyImportedWeeks(menuDrafts.breakfast, byMeal.breakfast);

    if (lunchDays === 0 && breakfastDays > 0 && activeMeal !== "breakfast") setActiveMeal("breakfast");
    renderActiveWeek();
    renderWeekChips();
    updateSaveLabel();
    updateMealBadges();

    const bits = [];
    if (lunchDays > 0) bits.push(`${lunchDays} lunch ${lunchDays === 1 ? "day" : "days"}`);
    if (breakfastDays > 0) bits.push(`${breakfastDays} breakfast ${breakfastDays === 1 ? "day" : "days"}`);
    setImportStatus(
      [
        `Got ${bits.join(" and ")} from SchoolCafe.`,
        json.message || "",
        "Check it over, then save."
      ].filter(Boolean).join(" "),
      "is-ok");
    trackEvent("cafeteria_provider_fetched", { days: lunchDays + breakfastDays });
  }

  async function providerFetch() {
    if (providerBusy || importInFlight || !menuDraft) return;
    providerBusy = true;
    setImportStatus("", null);
    setImportBusy(true, "Fetching from SchoolCafe…");

    const result = await api("POST", "/api/cafeteria/provider-fetch", {
      schoolKey: menuSchoolKey(),
      weekStart: menuDrafts.lunch.target
    });

    providerBusy = false;
    setImportBusy(false);

    if (!result.ok) {
      setImportStatus(errorMessage(result, "SchoolCafe didn't answer just now — try again in a minute."), "is-error");
      trackEvent("cafeteria_provider_fetch_failed", { status: result.status });
      return;
    }

    applyProviderWeeks(result.json || {});
  }

  if (menuDialog) {
    menuDialog.querySelectorAll("[data-caf-mday-noschool]").forEach((toggle) => {
      toggle.addEventListener("click", () => {
        captureActiveWeek();
        const fieldset = toggle.closest("[data-caf-mday]");
        const rows = menuDraft && menuDraft.weeks[menuDraft.active];
        if (!fieldset || !rows) return;
        const index = Number(fieldset.dataset.cafMday);
        rows[index].noSchool = !rows[index].noSchool;
        markDirty(menuDraft, menuDraft.active);
        // Repaint: the capture above may have just turned half-typed text into a chip, and a dish on
        // screen nowhere but in the draft is a dish that gets saved without the parent ever seeing it.
        paintChips(fieldset, rows[index]);
        paintNoSchool(fieldset, rows[index].noSchool);
        updateWeekChipStates();
        updateSaveLabel();
      });
    });
  }

  if (menuDialog) {
    // Enter commits a dish. Deliberately not blur: a parent tabbing between days would otherwise
    // commit half-typed text, and the pending value is swept up on save anyway.
    menuDialog.addEventListener("keydown", (event) => {
      const input = event.target.closest("[data-caf-mday-input]");
      if (!input || event.key !== "Enter") return;
      event.preventDefault();
      const fieldset = input.closest("[data-caf-mday]");
      const rows = menuDraft && menuDraft.weeks[menuDraft.active];
      if (!rows) return;
      const index = Number(fieldset.dataset.cafMday);
      const group = input.dataset.cafMdayInput;
      const refused = addMenuItem(rows[index], group, input.value);

      if (refused === "full") {
        // Keep the text: unlike a duplicate, this one is NOT already on the day, so clearing it would
        // be the silent drop we are trying to avoid.
        setImportStatus(
          `That day already lists ${MAX_ITEMS_PER_LIST} ${group === "sides" ? "sides" : "choices"} — `
          + "remove one to add another.", "is-error");
        return;
      }

      // Cleared for a duplicate too: the dish IS already on the day, and leaving the text sitting there
      // made Enter look broken while the value got discarded without a word on save.
      input.value = "";
      if (refused) return;

      markDirty(menuDraft, menuDraft.active);
      paintChips(fieldset, rows[index]);
      updateWeekChipStates();
      updateSaveLabel();
    });

    menuDialog.addEventListener("click", (event) => {
      const remove = event.target.closest("[data-caf-mday-remove]");
      if (!remove) return;
      const fieldset = remove.closest("[data-caf-mday]");
      const rows = menuDraft && menuDraft.weeks[menuDraft.active];
      if (!rows) return;
      const index = Number(fieldset.dataset.cafMday);
      rows[index][remove.dataset.group].splice(Number(remove.dataset.cafMdayRemove), 1);
      markDirty(menuDraft, menuDraft.active);
      paintChips(fieldset, rows[index]);
      updateWeekChipStates();
      updateSaveLabel();
      // Focus lands back on the day's add box: removing a chip destroys the button that had focus,
      // which would otherwise drop a keyboard user to the top of the dialog.
      fieldset.querySelector(`[data-caf-mday-input="${remove.dataset.group}"]`).focus();
    });
  }

  const importRevealBtn = document.querySelector("[data-caf-import-reveal-btn]");
  if (importRevealBtn) {
    importRevealBtn.addEventListener("click", () => {
      const reveal = document.querySelector("[data-caf-import-reveal]");
      reveal.dataset.revealed = "1";
      renderProviderUi();
    });
  }

  // ── School finder: state → district → school → grade ──────────────
  //
  // SchoolCafe's menu API is keyed on a school GUID that appears nowhere a parent can copy — not in the
  // page URL, only in the request the site makes to itself. Rather than ask for something they cannot
  // get, this walks the same directory their own school-finder walks.
  if (providerRoot) {
    const stateSelect = providerRoot.querySelector("[data-caf-provider-state]");
    const districtInput = providerRoot.querySelector("[data-caf-provider-district]");
    const districtResults = providerRoot.querySelector("[data-caf-provider-district-results]");
    const districtStatus = providerRoot.querySelector("[data-caf-provider-district-status]");
    const schoolSelect = providerRoot.querySelector("[data-caf-provider-school]");
    const gradeSelect = providerRoot.querySelector("[data-caf-provider-grade]");
    const connectBtn = providerRoot.querySelector("[data-caf-provider-connect-btn]");
    let districtTimer = null;
    let districtSeq = 0;

    function showStep(name, visible) {
      const step = providerRoot.querySelector(`[data-caf-picker-step="${name}"]`);
      if (step) step.hidden = !visible;
    }

    function setDistrictStatus(text) {
      districtStatus.textContent = text || "";
      districtStatus.hidden = !text;
    }

    function refreshConnectEnabled() {
      connectBtn.disabled = !schoolSelect.value || !gradeSelect.value;
    }

    function fillOptions(select, values, placeholder) {
      select.textContent = "";
      const blank = document.createElement("option");
      blank.value = "";
      blank.textContent = placeholder;
      select.append(blank);
      values.forEach(({ value, label }) => {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = label;
        select.append(option);
      });
    }

    stateSelect.addEventListener("change", () => {
      districtInput.value = "";
      districtResults.textContent = "";
      districtResults.hidden = true;
      setDistrictStatus("");
      showStep("district", !!stateSelect.value);
      showStep("school", false);
      refreshConnectEnabled();
    });

    // Enter in the district box means "search now", ahead of the debounce. (Enter is stopped from
    // submitting the form at the form level — see the guard on #caf-menu-form.)
    districtInput.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      window.clearTimeout(districtTimer);
      runDistrictSearch();
    });

    districtInput.addEventListener("input", () => {
      window.clearTimeout(districtTimer);
      const query = districtInput.value.trim();
      districtResults.hidden = true;
      showStep("school", false);
      refreshConnectEnabled();

      // Two characters is where the provider's own finder starts being useful, and the delay keeps a
      // district name from costing one upstream call per keystroke.
      if (query.length < 2) {
        setDistrictStatus("");
        return;
      }

      setDistrictStatus("Searching…");
      districtTimer = window.setTimeout(runDistrictSearch, 450);
    });

    async function runDistrictSearch() {
      const query = districtInput.value.trim();
      if (query.length < 2 || !stateSelect.value) return;

      setDistrictStatus("Searching…");
      {
        const seq = ++districtSeq;
        const result = await api(
          "GET",
          `/api/cafeteria/provider/districts?state=${encodeURIComponent(stateSelect.value)}&q=${encodeURIComponent(query)}`);
        // A slower earlier request must not overwrite a newer answer.
        if (seq !== districtSeq) return;

        districtResults.textContent = "";

        // A failed call is not the same fact as an empty result. Collapsing them told a parent whose
        // session had expired that their district isn't on SchoolCafe — which is the exact mistake the
        // Accept: application/json header was added to stop.
        if (!result.ok) {
          setDistrictStatus(errorMessage(result, "We couldn't reach SchoolCafe just now — try again."));
          districtResults.hidden = true;
          districtInput.focus();
          return;
        }

        const districts = (result.json && result.json.districts) || [];
        if (districts.length === 0) {
          // Never a dead end: not every district is on SchoolCafe, and a parent whose isn't needs to be
          // pointed at the path that will work rather than left staring at "no matches".
          setDistrictStatus(
            "No districts matched. Try just the county or city name — or add the menu yourself with "
            + "the link below.");
          districtResults.hidden = true;
          districtInput.focus();
          return;
        }

        // Announced, not cleared: this is the moment the region has something worth saying, and the
        // results below it are a visual-only change to a screen reader.
        setDistrictStatus(districts.length === 1
          ? "1 district found — choose it below."
          : `${districts.length} districts found — choose yours below.`);
        districts.forEach((district) => {
          const item = document.createElement("li");
          const button = document.createElement("button");
          button.type = "button";
          button.className = "caf-picker-result";
          button.textContent = district.name;
          if (!district.publishesMenus) {
            const note = document.createElement("span");
            note.className = "caf-picker-result-note";
            note.textContent = "no published menus";
            button.append(note);
          }
          button.addEventListener("click", () => selectDistrict(district));
          item.append(button);
          districtResults.append(item);
        });
        districtResults.hidden = false;
      }
    }

    async function selectDistrict(district) {
      districtInput.value = district.name;
      districtResults.hidden = true;
      setDistrictStatus("Loading schools…");
      showStep("school", false);

      const result = await api("GET", `/api/cafeteria/provider/schools?districtId=${encodeURIComponent(district.id)}`);
      const schools = (result.ok && result.json && result.json.schools) || [];
      const grades = (result.ok && result.json && result.json.grades) || [];

      if (schools.length === 0) {
        setDistrictStatus("We couldn't load that district's schools. Try again in a moment.");
        districtInput.focus();
        return;
      }

      setDistrictStatus("");
      fillOptions(
        schoolSelect,
        schools.map((s) => ({ value: s.id, label: s.siteType ? `${s.name} · ${s.siteType}` : s.name })),
        "Choose a school");
      fillOptions(gradeSelect, grades.map((g) => ({ value: g, label: g })), "Choose a grade");
      // We already know the grade — it is on the kid. Asking again when we can answer is exactly the
      // kind of step that makes a short flow feel long.
      const known = matchGrade(grades, kidGradeForSchool());
      if (known) gradeSelect.value = known;
      showStep("school", true);
      refreshConnectEnabled();
      schoolSelect.focus();
    }

    /** The grade recorded on a kid at the school being connected, if any. */
    function kidGradeForSchool() {
      const schoolKey = menuSchoolKey();
      const kid = (getData().kids || []).find((k) => k.schoolKey === schoolKey && k.grade);
      return kid ? String(kid.grade).trim() : "";
    }

    /** SchoolCafe writes "01" and "KG"; a family profile says "1" or "K". Match across that gap. */
    function matchGrade(grades, wanted) {
      if (!wanted) return "";
      const lower = wanted.toLowerCase();
      const padded = lower.length === 1 && lower >= "0" && lower <= "9" ? `0${lower}` : lower;
      const aliases = { k: "kg", pk: "pk", kg: "kg" };
      const target = aliases[lower] || padded;
      return grades.find((g) => g.toLowerCase() === target) || "";
    }

    providerRoot.querySelector("[data-caf-provider-skip]").addEventListener("click", () => {
      dismissFinder(menuSchoolKey());
      renderProviderUi();
      renderActiveWeek();
      updateSaveLabel();
      const firstEntree = menuDialog.querySelector('[data-caf-mday-input="entrees"]');
      if (firstEntree) firstEntree.focus();
    });

    const reopenBtn = document.querySelector("[data-caf-provider-reopen-btn]");
    if (reopenBtn) {
      reopenBtn.addEventListener("click", () => {
        finderReopened = menuSchoolKey();
        renderProviderUi();
        providerRoot.querySelector("[data-caf-provider-state]").focus();
      });
    }

    schoolSelect.addEventListener("change", refreshConnectEnabled);
    gradeSelect.addEventListener("change", refreshConnectEnabled);

    providerRoot.querySelector("[data-caf-provider-fetch]").addEventListener("click", providerFetch);

    providerRoot.querySelector("[data-caf-provider-connect-btn]").addEventListener("click", async () => {
      if (providerBusy) return;
      const schoolSelect = providerRoot.querySelector("[data-caf-provider-school]");
      const gradeSelect = providerRoot.querySelector("[data-caf-provider-grade]");
      const providerSchoolId = schoolSelect.value;
      const grade = gradeSelect.value;
      if (!providerSchoolId) {
        setImportStatus("Choose your school first.", "is-error");
        return;
      }

      providerBusy = true;
      setImportBusy(true, "Checking that school with SchoolCafe…");
      const result = await api("PUT", "/api/cafeteria/provider-link", {
        schoolKey: menuSchoolKey(),
        providerSchoolId,
        grade
      });
      providerBusy = false;
      setImportBusy(false);

      if (!result.ok) {
        setImportStatus(errorMessage(result, "We couldn't connect that school — try picking it again."), "is-error");
        return;
      }

      providerLinksOverride = allProviderLinks().filter((l) => l.schoolKey !== result.json.schoolKey).concat([result.json]);
      renderProviderUi();
      // The Connect button that had focus is inside the card just hidden; without this the parent is
      // dropped to the top of the document for the whole fetch that follows.
      providerRoot.querySelector("[data-caf-provider-fetch]").focus();
      trackEvent("cafeteria_provider_linked", {});
      // Straight into the first fetch: connecting and then staring at an unchanged form would read as
      // "did that work?" — the same in-your-face rule the import learned.
      await providerFetch();
    });

    providerRoot.querySelector("[data-caf-provider-unlink]").addEventListener("click", async () => {
      if (providerBusy) return;
      const schoolKey = menuSchoolKey();
      const result = await api("DELETE", `/api/cafeteria/provider-link/${encodeURIComponent(schoolKey)}`);
      if (result.ok || result.status === 404) {
        providerLinksOverride = allProviderLinks().filter((l) => l.schoolKey !== schoolKey);
        renderProviderUi();
        setImportStatus("Disconnected from SchoolCafe. You can reconnect any time.", null);
      }
    });
  }

  // ── Menu import ───────────────────────────────────────────────

  const importInput = document.querySelector("[data-caf-import-input]");
  const importDrop = document.querySelector("[data-caf-import-drop]");
  const importStatus = document.querySelector("[data-caf-import-status]");
  let importInFlight = false;

  function setImportStatus(text, tone) {
    if (!importStatus) return;
    importStatus.textContent = text || "";
    importStatus.hidden = !text;
    importStatus.classList.remove("is-error", "is-busy", "is-ok");
    if (tone) importStatus.classList.add(tone);
  }

  /**
   * The reading state has to be unmissable — the first live test's feedback was "hard to tell it was
   * working". While reading: the drop zone swaps its icon for a spinner and its title for a progress
   * line, and the day fields dim behind aria-busy so the eye goes to the one thing that is moving.
   */
  function setImportBusy(busy, progressText) {
    const container = document.querySelector("[data-caf-import]");
    const spinner = document.querySelector("[data-caf-import-spinner]");
    const title = document.querySelector("[data-caf-import-title]");
    const days = menuDialog && menuDialog.querySelector(".caf-menu-days");
    const save = document.getElementById("caf-menu-save");

    if (container) container.classList.toggle("is-reading", busy);
    if (spinner) spinner.hidden = !busy;
    if (title) title.textContent = busy
      ? (progressText || "Reading your menu…")
      : "Have the menu as a file? Drop it here";
    if (days) {
      days.classList.toggle("is-reading", busy);
      if (busy) days.setAttribute("aria-busy", "true");
      else days.removeAttribute("aria-busy");
    }
    if (save) save.disabled = busy;
  }

  /**
   * "Save menu" or "Save N weeks" across BOTH meals — the button states the write's whole scope.
   * Lunch writes its target when the target holds something OR had a stored menu (that is how a menu is
   * cleared); breakfast writes only content or user-edited weeks, so opening the dialog can never erase
   * a stored breakfast as a side effect. An empty target that never had a menu is not a write at all —
   * counting it is what made a one-week import offer to "Save 2 weeks".
   */
  function pendingSaves() {
    if (!menuDrafts.lunch) return [];
    return [
      ...weeksToSave(menuDrafts.lunch, true).map((iso) => ({ meal: "lunch", iso })),
      ...weeksToSave(menuDrafts.breakfast, false).map((iso) => ({ meal: "breakfast", iso }))
    ];
  }

  function updateSaveLabel() {
    const save = document.getElementById("caf-menu-save");
    if (!save || !menuDraft) return;
    // Distinct calendar weeks, not writes. A week with both meals is two writes but one week to a
    // parent, and "Save 2 weeks" over a single week of menu reads as a bug in the import.
    const weeks = new Set(pendingSaves().map((p) => p.iso));
    save.textContent = weeks.size > 1 ? `Save ${weeks.size} weeks` : "Save menu";
  }

  function resetImportUi() {
    setImportStatus("", null);
    setImportBusy(false);
    if (importInput) importInput.value = "";
    if (importDrop) importDrop.classList.remove("is-dragover");
  }

  /** One provider round trip. Reports back rather than painting status, so the batch loop owns the UI. */
  async function importOneFile(file) {
    const body = new FormData();
    body.append("file", file, file.name);

    let response;
    let json = null;
    try {
      // Only the CSRF header: setting Content-Type by hand would strip the multipart boundary the
      // browser generates, and the server would parse an empty form.
      const token = getCsrfToken();
      response = await fetch("/api/cafeteria/menu-import", {
        method: "POST",
        headers: token ? { "X-CSRF-TOKEN": token } : {},
        body
      });
      try {
        json = await response.json();
      } catch {
        /* an error page or an empty body */
      }
    } catch {
      return { ok: false, weeks: [], message: "We couldn't reach the server.", fatal: true };
    }

    if (!response.ok) {
      trackEvent("cafeteria_menu_import_failed", { status: response.status });
      return {
        ok: false,
        weeks: [],
        message: importErrorMessage(response.status, json),
        // A rate limit answers every further file identically — stop the batch instead of burning it.
        fatal: response.status === 429
      };
    }

    return { ok: true, weeks: (json && json.weeks) || [], message: json && json.message };
  }

  // More than a school year of menus in one sitting is a mistake, not a batch.
  const MAX_IMPORT_FILES = 4;

  /**
   * Reads a batch of files — a parent importing "the whole month" usually holds one PDF or several
   * weekly screenshots — and merges every week they produce into the draft. Files are read one at a
   * time: each is its own provider call and its own slice of the rate budget, and the progress line
   * counts them so a four-file read never looks stalled.
   */
  async function importMenuFiles(fileList) {
    if (importInFlight || !menuDraft) return;
    const files = Array.from(fileList || []).slice(0, MAX_IMPORT_FILES);
    if (files.length === 0) return;

    const limits = getData().menuImport || {};
    const oversized = limits.maxBytes && files.find((f) => f.size > limits.maxBytes);
    if (oversized) {
      setImportStatus(
        `"${oversized.name}" is larger than ${Math.floor(limits.maxBytes / (1024 * 1024))} MB. Try a photo of just the menu page.`,
        "is-error");
      return;
    }

    importInFlight = true;
    captureActiveWeek();
    setImportStatus("", null);

    let daysFilled = 0;
    let breakfastDaysFilled = 0;
    const weeksTouched = new Set();
    const notes = [];
    const failures = [];

    for (let i = 0; i < files.length; i++) {
      setImportBusy(true, files.length > 1
        ? `Reading file ${i + 1} of ${files.length}…`
        : "Reading your menu — usually a few seconds…");

      const result = await importOneFile(files[i]);

      if (result.ok && result.weeks.length > 0) {
        // Meals never share a dish list: district PDFs print breakfast and lunch side by side, and each
        // set lands in its own draft behind the meal toggle.
        const byMeal = splitWeeksByMeal(result.weeks);
        daysFilled += applyImportedWeeks(menuDrafts.lunch, byMeal.lunch);
        breakfastDaysFilled += applyImportedWeeks(menuDrafts.breakfast, byMeal.breakfast);
        result.weeks.forEach((w) => weeksTouched.add(w.weekStartIso));
        if (result.message) notes.push(result.message);
      } else {
        failures.push({ name: files[i].name, message: result.message });
        if (result.fatal) break;
      }
    }

    importInFlight = false;
    setImportBusy(false);
    if (importInput) importInput.value = "";

    // A breakfast-only file must not end with the parent staring at an unchanged lunch form wondering
    // whether anything happened — land them on the meal the file actually filled.
    if (daysFilled === 0 && breakfastDaysFilled > 0 && activeMeal !== "breakfast") {
      setActiveMeal("breakfast");
    }
    renderActiveWeek();
    renderWeekChips();
    updateSaveLabel();
    updateMealBadges();

    if (daysFilled + breakfastDaysFilled === 0) {
      const detail = failures.length > 0 && failures[0].message
        ? failures[0].message
        : "We couldn't find a lunch menu in there. Enter the week by hand below.";
      setImportStatus(detail, "is-error");
      return;
    }

    const mealBits = [];
    if (daysFilled > 0) mealBits.push(`${daysFilled} lunch ${daysFilled === 1 ? "day" : "days"}`);
    if (breakfastDaysFilled > 0) mealBits.push(`${breakfastDaysFilled} breakfast ${breakfastDaysFilled === 1 ? "day" : "days"}`);
    const parts = [
      `Filled ${mealBits.join(" and ")} across ${weeksTouched.size} ${weeksTouched.size === 1 ? "week" : "weeks"}.`
    ];
    if (failures.length > 0) {
      parts.push(`${failures.length} ${failures.length === 1 ? "file" : "files"} couldn't be read.`);
    }
    // Deduped: every file in a batch tends to produce the same partial-read note, and hearing
    // "2 days were skipped" twice reads as 4.
    parts.push(...new Set(notes));
    parts.push(weeksTouched.size > 1
      ? "Flip through the weeks below — Save writes them all."
      : "Check it over, then save.");
    setImportStatus(parts.join(" "), "is-ok");

    trackEvent("cafeteria_menu_imported", {
      days: daysFilled, breakfastDays: breakfastDaysFilled, weeks: weeksTouched.size, files: files.length
    });
  }

  if (importInput) {
    importInput.addEventListener("change", () => {
      if (importInput.files && importInput.files.length) importMenuFiles(importInput.files);
    });
  }

  if (importDrop) {
    ["dragenter", "dragover"].forEach((name) => {
      importDrop.addEventListener(name, (event) => {
        event.preventDefault();
        importDrop.classList.add("is-dragover");
      });
    });

    ["dragleave", "dragend"].forEach((name) => {
      importDrop.addEventListener(name, () => importDrop.classList.remove("is-dragover"));
    });

    importDrop.addEventListener("drop", (event) => {
      event.preventDefault();
      importDrop.classList.remove("is-dragover");
      if (event.dataTransfer && event.dataTransfer.files) importMenuFiles(event.dataTransfer.files);
    });
  }

  document.querySelectorAll("[data-caf-meal]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const meal = btn.dataset.cafMeal;
      if (!menuDrafts[meal] || meal === activeMeal) return;
      captureActiveWeek();
      setActiveMeal(meal);
      renderActiveWeek();
      renderWeekChips();
      updateSaveLabel();
    });
  });

  const importWeekChips = document.querySelector("[data-caf-import-week-chips]");
  if (importWeekChips) {
    importWeekChips.addEventListener("click", (event) => {
      const chip = event.target.closest("[data-caf-import-week]");
      if (chip) selectWeek(chip.dataset.cafImportWeek);
    });
  }

  // ── Menu save ─────────────────────────────────────────────────

  const menuForm = document.getElementById("caf-menu-form");

  // Every control in this dialog lives in one form, so Enter on any of them implicitly submits — which
  // saves and reloads the page, throwing away a half-finished school selection or a chip mid-typing.
  // Nothing here should submit on Enter; Save is an explicit click. Buttons are exempt so Enter still
  // activates them.
  if (menuForm) {
    menuForm.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.target.closest("button")) event.preventDefault();
    });
  }
  if (menuForm) {
    // Typing into a blank imported week changes which weeks a save writes; the button label follows.
    // Dirty is what lets a breakfast week be CLEARED: emptied-by-hand has no content but has intent.
    menuForm.addEventListener("input", () => {
      if (!menuDraft || importInFlight) return;
      // Deliberately NOT captureActiveWeek(): that commits whatever is half-typed and clears the box.
      // It was safe to call here when it was an idempotent copy of the old text fields; against the
      // chip model it ate every keystroke. Pending text is committed on Enter, and swept up on save,
      // week switch, meal switch and the no-school toggle — never mid-word.
      markDirty(menuDraft, menuDraft.active);
      updateSaveLabel();
      updateMealBadges();
    });

    menuForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!menuDraft || importInFlight) return;
      captureActiveWeek();

      const pending = pendingSaves();

      const save = document.getElementById("caf-menu-save");
      const message = document.getElementById("caf-menu-message");
      const schoolKey = menuSchoolKey();
      const saveLabel = save.textContent;
      save.disabled = true;

      for (let i = 0; i < pending.length; i++) {
        const { meal, iso } = pending[i];
        if (pending.length > 1) save.textContent = `Saving ${i + 1} of ${pending.length}…`;

        const result = await api("PUT", `/api/cafeteria/menu-weeks/${iso}`, {
          schoolKey,
          meal,
          days: weekPayload(iso, menuDrafts[meal].weeks[iso])
        });

        if (!result.ok) {
          save.disabled = false;
          save.textContent = saveLabel;
          // Name the week AND meal that failed: with several in flight, "couldn't save the menu" leaves
          // a parent unable to tell which of them to redo.
          message.textContent = errorMessage(
            result,
            pending.length > 1
              ? `Couldn't save ${meal === "breakfast" ? "breakfast" : "lunch"} for the week of ${weekLabel(iso)} — try again.`
              : "Couldn't save the menu — try again.");
          message.hidden = false;
          return;
        }
      }

      if (pending.length === 0) {
        // Nothing changed — closing is the honest response. Reloading the page for a no-op write reads
        // as "something happened", which is the opposite of true.
        save.disabled = false;
        menuDialog.close();
        return;
      }

      trackEvent("cafeteria_menu_saved", {
        weeks: pending.length,
        imported: menuDrafts.lunch.imported || menuDrafts.breakfast.imported
      });
      window.location.reload();
    });
  }

  // ── Kid dialog ────────────────────────────────────────────────

  let kidTarget = { kidId: null };

  function openKidDialog(kidId) {
    if (!kidDialog) return;
    kidTarget = { kidId: kidId || null };
    const kid = kidId ? (getData().kids || []).find((k) => k.id === kidId) : null;

    kidDialog.querySelector("[data-caf-kid-title]").textContent = kid ? `Edit ${kid.name}` : "Add a kid";
    document.getElementById("caf-kid-name").value = kid ? kid.name : "";
    document.getElementById("caf-kid-school").value = kid ? kid.schoolName || "" : "";
    document.getElementById("caf-kid-grade").value = kid ? kid.grade || "" : "";
    document.getElementById("caf-kid-price").value = kid ? kid.priceInput : "3.00";
    document.getElementById("caf-kid-balance").value = "";
    kidDialog.querySelector("[data-caf-kid-balance-field]").hidden = !!kid;
    document.getElementById("caf-kid-delete").hidden = !kid;
    document.getElementById("caf-kid-message").hidden = true;

    const used = (getData().kids || []).map((k) => k.colorKey);
    const targetColor = kid ? kid.colorKey : null;
    const radios = Array.from(kidDialog.querySelectorAll('input[name="caf-kid-color"]'));
    const firstUnused = radios.map((r) => r.value).find((v) => !used.includes(v)) || radios[0].value;
    radios.forEach((radio) => {
      radio.checked = radio.value === (targetColor || firstUnused);
    });

    kidDialog.showModal();
  }

  const kidForm = document.getElementById("caf-kid-form");
  if (kidForm) {
    kidForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      const message = document.getElementById("caf-kid-message");
      const name = document.getElementById("caf-kid-name").value.trim();
      if (!name) {
        message.textContent = "Name is required.";
        message.hidden = false;
        return;
      }

      const color = kidDialog.querySelector('input[name="caf-kid-color"]:checked');
      const body = {
        displayName: name,
        schoolName: document.getElementById("caf-kid-school").value.trim() || null,
        grade: document.getElementById("caf-kid-grade").value.trim() || null,
        colorKey: color ? color.value : null,
        mealPriceCents: dollarsToCents(document.getElementById("caf-kid-price").value)
      };
      if (!kidTarget.kidId) {
        body.initialBalanceCents = dollarsToCents(document.getElementById("caf-kid-balance").value);
      }

      const save = document.getElementById("caf-kid-save");
      save.disabled = true;
      const result = kidTarget.kidId
        ? await api("PUT", `/api/cafeteria/kids/${encodeURIComponent(kidTarget.kidId)}`, body)
        : await api("POST", "/api/cafeteria/kids", body);
      save.disabled = false;

      if (!result.ok) {
        message.textContent = errorMessage(result, "Couldn't save — try again.");
        message.hidden = false;
        return;
      }

      trackEvent(kidTarget.kidId ? "cafeteria_kid_updated" : "cafeteria_kid_added", { context: "dialog" });
      window.location.reload();
    });
  }

  const kidDelete = document.getElementById("caf-kid-delete");
  if (kidDelete) {
    kidDelete.addEventListener("click", () => {
      if (!kidTarget.kidId) return;
      kidDialog.close();
      document.getElementById("caf-confirm-message").hidden = true;
      confirmDialog.showModal();
    });
  }

  const confirmYes = document.getElementById("caf-confirm-yes");
  if (confirmYes) {
    confirmYes.addEventListener("click", async () => {
      if (!kidTarget.kidId) return;
      confirmYes.disabled = true;
      const result = await api("DELETE", `/api/cafeteria/kids/${encodeURIComponent(kidTarget.kidId)}`);
      confirmYes.disabled = false;

      if (!result.ok) {
        const message = document.getElementById("caf-confirm-message");
        message.textContent = errorMessage(result, "Couldn't remove — try again.");
        message.hidden = false;
        return;
      }

      window.location.reload();
    });
  }

  // ── Balance history dialog ────────────────────────────────────

  async function openHistoryDialog(kidId, kidName) {
    if (!historyDialog) return;
    historyDialog.querySelector("[data-caf-history-title]").textContent = `Balance history — ${kidName}`;
    const status = historyDialog.querySelector("[data-caf-history-status]");
    const list = historyDialog.querySelector("[data-caf-history-list]");
    status.textContent = "Loading…";
    status.hidden = false;
    list.textContent = "";
    historyDialog.showModal();

    const result = await api("GET", `/api/cafeteria/kids/${encodeURIComponent(kidId)}/wallet-entries?limit=20`);
    if (!result.ok || !result.json) {
      status.textContent = "Couldn't load the history — close and try again.";
      return;
    }

    const entries = result.json.entries || [];
    if (entries.length === 0) {
      status.textContent = "No balance changes recorded yet.";
      return;
    }

    status.hidden = true;
    entries.forEach((entry) => {
      const item = document.createElement("li");
      item.className = "caf-history-item";
      const date = document.createElement("span");
      date.className = "caf-history-date";
      date.textContent = entry.dateLabel;
      const label = document.createElement("span");
      label.textContent = entry.typeLabel + (entry.note ? ` — ${entry.note}` : "");
      const delta = document.createElement("span");
      delta.className = "caf-history-delta";
      delta.textContent = `${entry.deltaDisplay} → ${entry.newBalanceDisplay}`;
      item.append(date, label, delta);
      list.append(item);
    });
  }

  // ── Delegated clicks ──────────────────────────────────────────

  document.addEventListener("click", (event) => {
    const decision = event.target.closest("[data-caf-decision]");
    if (decision) {
      if (!decision.classList.contains("caf-seg-btn--active")) {
        setDecision(decision.dataset.kidId, decision.dataset.date, decision.dataset.decision);
      }
      return;
    }

    const switchBtn = event.target.closest("[data-caf-switch]");
    if (switchBtn) {
      setDecision(switchBtn.dataset.kidId, switchBtn.dataset.date, switchBtn.dataset.decision);
      return;
    }

    const menuOpen = event.target.closest("[data-caf-menu-open]");
    if (menuOpen) {
      openMenuDialog(menuOpen.dataset.weekStart || getData().weekStartIso, menuOpen.dataset.schoolKey || null);
      return;
    }

    const kidOpen = event.target.closest("[data-caf-kid-open]");
    if (kidOpen) {
      openKidDialog(kidOpen.dataset.kidId || null);
      return;
    }

    const historyOpen = event.target.closest("[data-caf-history-open]");
    if (historyOpen) {
      openHistoryDialog(historyOpen.dataset.kidId, historyOpen.dataset.kidName);
      return;
    }

    const balanceEdit = event.target.closest("[data-caf-balance-edit]");
    if (balanceEdit) {
      openBalanceEditor(balanceEdit.closest("[data-caf-wallet]"));
      return;
    }

    const balanceCancel = event.target.closest("[data-caf-balance-cancel]");
    if (balanceCancel) {
      closeBalanceEditor(balanceCancel.closest("[data-caf-wallet]"));
      return;
    }

    const focusBalance = event.target.closest("[data-caf-focus-balance]");
    if (focusBalance) {
      activateView("today");
      const card = root.querySelector(`[data-caf-wallet][data-kid-id="${focusBalance.dataset.kidId}"]`)
        || root.querySelector("[data-caf-wallet]");
      if (card) {
        card.scrollIntoView({ block: "center" });
        openBalanceEditor(card);
      }
      return;
    }

    const gotoView = event.target.closest("[data-caf-goto]");
    if (gotoView) {
      activateView(gotoView.dataset.cafGoto);
      return;
    }

    const chipAdd = event.target.closest("[data-caf-chip-add]");
    if (chipAdd) {
      const input = chipAdd.closest("[data-caf-chip-row]").querySelector("[data-caf-chip-input]");
      input.hidden = false;
      input.focus();
      return;
    }

    const chipRemove = event.target.closest("[data-caf-chip-remove]");
    if (chipRemove) {
      const chip = chipRemove.closest("[data-caf-chip]");
      const prefCard = chipRemove.closest("[data-caf-prefs]");
      const kind = chipRemove.closest("[data-caf-pref-group]").dataset.kind;
      const previous = collectTerms(prefCard, kind);
      chip.remove();
      savePreferences(prefCard, kind).then((saved) => {
        if (!saved) renderChips(prefCard, kind, previous);
      });
    }
  });
}
